<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import {
  CHANNEL_HEALTH_STATUS,
  channelHealthForInbox,
  channelIconForInbox,
  channelLabelKeyForInbox,
  channelPresentationForInbox,
  isWhatsappCloudInbox,
} from './helpers/channel';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
  defaultExpanded: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const isExpanded = ref(props.defaultExpanded);
const remoteHealthByInboxId = ref({});

const severityOrder = {
  [CHANNEL_HEALTH_STATUS.ATTENTION]: 0,
  [CHANNEL_HEALTH_STATUS.WARNING]: 1,
  [CHANNEL_HEALTH_STATUS.UNKNOWN]: 2,
  [CHANNEL_HEALTH_STATUS.OK]: 3,
};

const channelLabel = inbox => {
  const labels = {
    'ESSENTIAL.CHANNELS.WHATSAPP': t('ESSENTIAL.CHANNELS.WHATSAPP'),
    'ESSENTIAL.CHANNELS.WHATSAPP_EVOLUTION': t(
      'ESSENTIAL.CHANNELS.WHATSAPP_EVOLUTION'
    ),
    'ESSENTIAL.CHANNELS.WHATSAPP_API': t('ESSENTIAL.CHANNELS.WHATSAPP_API'),
    'ESSENTIAL.CHANNELS.TELEGRAM': t('ESSENTIAL.CHANNELS.TELEGRAM'),
    'ESSENTIAL.CHANNELS.EMAIL': t('ESSENTIAL.CHANNELS.EMAIL'),
    'ESSENTIAL.CHANNELS.SITE': t('ESSENTIAL.CHANNELS.SITE'),
    'ESSENTIAL.CHANNELS.OTHER': t('ESSENTIAL.CHANNELS.OTHER'),
  };
  return (
    labels[channelLabelKeyForInbox(inbox)] || t('ESSENTIAL.CHANNELS.OTHER')
  );
};

const reasonLabel = reasonKey => {
  const labels = {
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.REAUTH_REQUIRED': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.REAUTH_REQUIRED'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.EVOLUTION_NEEDS_ATTENTION': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.EVOLUTION_NEEDS_ATTENTION'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.EVOLUTION_CHECK_SESSION': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.EVOLUTION_CHECK_SESSION'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.MISSING_PHONE': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.MISSING_PHONE'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.WHATSAPP_READY': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.WHATSAPP_READY'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_HEALTH_LOADING': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_HEALTH_LOADING'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_HEALTH_UNAVAILABLE': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_HEALTH_UNAVAILABLE'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_REGISTRATION_INCOMPLETE': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_REGISTRATION_INCOMPLETE'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_PHONE_UNVERIFIED': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_PHONE_UNVERIFIED'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_NAME_REJECTED': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_NAME_REJECTED'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_QUALITY_RED': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_QUALITY_RED'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_QUALITY_YELLOW': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_QUALITY_YELLOW'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_NAME_PENDING': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_NAME_PENDING'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_WEBHOOK_MISMATCH': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_WEBHOOK_MISMATCH'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_READY': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.CLOUD_READY'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.TELEGRAM_CHECK_WEBHOOK': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.TELEGRAM_CHECK_WEBHOOK'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.TELEGRAM_READY': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.TELEGRAM_READY'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.EMAIL_CHECK_ADDRESS': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.EMAIL_CHECK_ADDRESS'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.EMAIL_READY': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.EMAIL_READY'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.SITE_CHECK_URL': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.SITE_CHECK_URL'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.SITE_READY': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.SITE_READY'
    ),
    'ESSENTIAL.CHANNEL_HEALTH.REASONS.UNKNOWN': t(
      'ESSENTIAL.CHANNEL_HEALTH.REASONS.UNKNOWN'
    ),
  };
  return labels[reasonKey] || t('ESSENTIAL.CHANNEL_HEALTH.REASONS.UNKNOWN');
};

const setRemoteHealth = (inboxId, value) => {
  remoteHealthByInboxId.value = {
    ...remoteHealthByInboxId.value,
    [inboxId]: value,
  };
};

const fetchRemoteHealth = async inbox => {
  if (!inbox?.id || !isWhatsappCloudInbox(inbox)) return;
  if (remoteHealthByInboxId.value[inbox.id]?.state === 'ok') return;

  setRemoteHealth(inbox.id, { state: 'loading' });

  try {
    const response = await InboxHealthAPI.getHealthStatus(inbox.id);
    setRemoteHealth(inbox.id, {
      state: 'ok',
      data: response.data,
    });
  } catch (error) {
    setRemoteHealth(inbox.id, {
      state: 'error',
      error: error.message,
    });
  }
};

watch(
  () => props.inboxes.map(inbox => `${inbox.id}:${inbox.provider}`).join('|'),
  () => {
    props.inboxes.filter(isWhatsappCloudInbox).forEach(fetchRemoteHealth);
  },
  { immediate: true }
);

const channelItems = computed(() =>
  props.inboxes
    .map(inbox => {
      const channelHealth = channelHealthForInbox(
        inbox,
        remoteHealthByInboxId.value[inbox.id]
      );
      const presentation = channelPresentationForInbox(inbox);
      return {
        ...channelHealth,
        id: inbox.id,
        name: inbox.name,
        channelIcon: channelIconForInbox(inbox),
        channelIconClass: presentation.iconClass,
        channelLabel: channelLabel(inbox),
        reason: reasonLabel(channelHealth.reasonKey),
      };
    })
    .sort((a, b) => severityOrder[a.status] - severityOrder[b.status])
);

const issueItems = computed(() =>
  channelItems.value.filter(item => item.status !== CHANNEL_HEALTH_STATUS.OK)
);

const summaryText = computed(() => {
  if (!channelItems.value.length) {
    return t('ESSENTIAL.CHANNEL_HEALTH.NO_CHANNELS');
  }
  if (!issueItems.value.length) return t('ESSENTIAL.CHANNEL_HEALTH.ALL_OK');

  return t('ESSENTIAL.CHANNEL_HEALTH.ISSUES', {
    count: issueItems.value.length,
  });
});

const statusClass = status => {
  const classes = {
    [CHANNEL_HEALTH_STATUS.OK]: 'bg-n-teal-3 text-n-teal-11',
    [CHANNEL_HEALTH_STATUS.WARNING]: 'bg-n-amber-3 text-n-amber-11',
    [CHANNEL_HEALTH_STATUS.ATTENTION]: 'bg-n-ruby-3 text-n-ruby-11',
    [CHANNEL_HEALTH_STATUS.UNKNOWN]: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[status] || classes[CHANNEL_HEALTH_STATUS.UNKNOWN];
};

const statusLabel = status => {
  const labels = {
    [CHANNEL_HEALTH_STATUS.OK]: t('ESSENTIAL.CHANNEL_HEALTH.STATUS.OK'),
    [CHANNEL_HEALTH_STATUS.WARNING]: t(
      'ESSENTIAL.CHANNEL_HEALTH.STATUS.WARNING'
    ),
    [CHANNEL_HEALTH_STATUS.ATTENTION]: t(
      'ESSENTIAL.CHANNEL_HEALTH.STATUS.ATTENTION'
    ),
    [CHANNEL_HEALTH_STATUS.UNKNOWN]: t(
      'ESSENTIAL.CHANNEL_HEALTH.STATUS.UNKNOWN'
    ),
  };
  return labels[status] || labels[CHANNEL_HEALTH_STATUS.UNKNOWN];
};
</script>

<template>
  <section class="flex flex-col gap-2 rounded-lg border border-n-weak p-3">
    <button
      type="button"
      class="flex items-center justify-between w-full gap-3 text-left"
      @click="isExpanded = !isExpanded"
    >
      <span class="flex items-center min-w-0 gap-2">
        <i class="i-lucide-activity size-4 text-n-slate-11" />
        <span class="text-xs font-medium uppercase text-n-slate-11">
          {{ $t('ESSENTIAL.CHANNEL_HEALTH.TITLE') }}
        </span>
      </span>
      <span class="flex items-center gap-2 text-xs text-n-slate-11">
        <span>{{ summaryText }}</span>
        <i
          class="i-lucide-chevron-down size-4 transition-transform"
          :class="{ 'rotate-180': isExpanded }"
        />
      </span>
    </button>

    <div v-if="isExpanded" class="flex flex-col gap-2 max-h-48 overflow-y-auto">
      <div
        v-for="item in channelItems"
        :key="item.id"
        class="flex items-start gap-2 rounded-md bg-n-alpha-1 p-2"
      >
        <i
          class="mt-0.5 size-4 flex-shrink-0"
          :class="[item.channelIcon, item.channelIconClass]"
        />
        <div class="min-w-0 flex-1">
          <div class="flex items-start justify-between gap-2">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ item.name }}
            </span>
            <span
              class="inline-flex flex-shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium"
              :class="statusClass(item.status)"
            >
              {{ statusLabel(item.status) }}
            </span>
          </div>
          <div class="flex items-center gap-1.5 text-xs text-n-slate-11">
            <span class="truncate">
              {{ item.channelLabel }}
            </span>
            <i class="size-1 rounded-full bg-n-slate-8 flex-shrink-0" />
            <span class="truncate">
              {{ item.reason }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
